import { StoreCode } from '@portal/schema/storeCode';
import type { CarouselMedia, Variant } from '@portal/state/store/types';
import { getFormattedShopifyId } from 'shared/utils/shopify/ids';
import type { BaseImage, BaseVideoWithPlayer, SanityKeyed } from 'types/studio/studio';
import type { ShopifyCartItem } from '../BuildBundleV3/types';
import { isPouchOrTravelPackV53Variant } from '@portal/shared/utils/shopify/isPouchOrTravelPackV53Variant';

export const flavorsMapper = {
  original: {
    title: 'Original',
    color: '#105C5C'
  },
  tropical: {
    title: 'Tropical',
    color: '#E3BE38'
  }
};

/**
 * Generates a unique identifier for a product variant by combining the first subscription variant ID
 * with the variant's quantity.
 *
 * @param variant - The product variant object containing subscription variant IDs and quantity
 * @returns A string in the format `{variantId}-{quantity}` or an empty string if no variant ID is found
 */
export const getUniqueVariantId = (variant: Variant, flavor?: string) => {
  const baseVariant = variant?.subscriptionVariantIds || variant?.oneTimeVariantId;
  let baseId = baseVariant?.[0].variantId;
  if (flavor !== 'original') {
    const flavorVariant = baseVariant?.find((subVariant) => subVariant.flavorQueryParam === flavor);
    if (flavorVariant) {
      baseId = flavorVariant.variantId;
    }
  }
  return baseId ? `${baseId}-${variant.quantity}` : '';
};

export const isValidVariantId = (variantId: string, flavor: string, variants: Variant[]) => {
  return (
    variantId &&
    variants.some((variant) => {
      return variantId === getUniqueVariantId(variant, flavor);
    })
  );
};

export function getSelectedVariant(
  variants: Variant[] | null | undefined,
  selectedVariantId: string,
  store: string,
  flavor?: string
): Variant | undefined {
  return variants
    ?.map((variant) => ({
      ...variant,
      subscriptionVariantIds: variant.subscriptionVariantIds?.filter(
        (item) => item.targetStore === store
      ),
      oneTimeVariantId: variant?.oneTimeVariantId?.filter((item) => item.targetStore === store)
    }))
    .find(
      (variant) =>
        variant.subscriptionVariantIds?.some(() => {
          const id = getUniqueVariantId(variant, flavor);
          return id === selectedVariantId;
        }) ||
        variant.oneTimeVariantId?.some(() => {
          const id = getUniqueVariantId(variant, flavor);
          return id === selectedVariantId;
        })
    );
}

export function getVariantsPerStore(
  variants: Variant[] | null | undefined,
  store: string
): Variant[] {
  if (!variants) return [];
  return variants
    .map((variant) => ({
      ...variant,
      subscriptionVariantIds: variant.subscriptionVariantIds?.filter(
        (item) => item.targetStore === store
      ),
      oneTimeVariantId: variant?.oneTimeVariantId?.filter((item) => item.targetStore === store)
    }))
    .filter(
      (variant) =>
        variant.subscriptionVariantIds?.length > 0 ||
        (variant.oneTimeVariantId && variant.oneTimeVariantId.length > 0)
    );
}

export function addPdpWelcomeKit(
  variant: Variant,
  storeCode: StoreCode,
  isOtp: boolean
): ShopifyCartItem[] {
  if (!variant.welcomeKitIds) return [];

  const targetWelcomeKit = variant.welcomeKitIds.find(
    ({ targetStore, isOTPKit }) => targetStore === storeCode && isOtp === !!isOTPKit
  );

  return targetWelcomeKit
    ? [
        {
          merchandiseId: getFormattedShopifyId('ProductVariant', targetWelcomeKit.welcomeKitId),
          quantity: 1,
          attributes: [
            {
              // double underscore makes the custom attribute hidden on the checkout
              key: isOtp ? '__custom.otp_welcome_kit' : '__custom.welcome_kit',
              value: 'true'
            }
          ]
        } as ShopifyCartItem
      ]
    : [];
}

type VariantIdLike = {
  variantId: string;
  flavorQueryParam?: string | null;
  _key: string;
};

export const getSubscriptionLineItems = (
  selectedVariant: Variant,
  quantity?: number,
  productCadence?: string,
  isOtpProduct?: boolean,
  flavor?: string,
  store?: StoreCode,
  isLoggedIn?: boolean
) => {
  const sellingPlanToSubmit = isOtpProduct
    ? 'otp'
    : productCadence || selectedVariant?.subscriptionVariantIds?.[0].sellingPlanId;

  // For OTP purchases of pouch/pack by logged-in users, use the subscription variant ID
  const isPouchOrPack = store
    ? isPouchOrTravelPackV53Variant(
        selectedVariant?.subscriptionVariantIds?.[0]?.variantId ||
          selectedVariant?.oneTimeVariantId?.[0]?.variantId ||
          '',
        store
      )
    : false;

  const shouldUseSubscriptionVariantForOtp = Boolean(isLoggedIn && isOtpProduct && isPouchOrPack);

  const selectedVariantIds =
    sellingPlanToSubmit === 'otp'
      ? shouldUseSubscriptionVariantForOtp
        ? selectedVariant.subscriptionVariantIds
        : selectedVariant.oneTimeVariantId
      : selectedVariant.subscriptionVariantIds;

  if (!selectedVariantIds?.length || !sellingPlanToSubmit || !selectedVariant) return [];

  // ---- Flavor-tolerant filtering ----
  const items: VariantIdLike[] = selectedVariantIds as unknown as VariantIdLike[];

  const normalize = (s?: string | null) => (s ?? '').trim().toLowerCase();
  const requestedFlavor = normalize(flavor);

  // What flavors does THIS PDP actually expose for thse variant ids?
  const availableFlavors = new Set(items.map((i) => normalize(i.flavorQueryParam)));
  const hasDefaultLane = items.some((i) => !i.flavorQueryParam);

  let variantIdsFilteredByFlavor = items;

  if (requestedFlavor) {
    if (availableFlavors.has(requestedFlavor)) {
      // Requested flavor exists here → filter to it
      variantIdsFilteredByFlavor = items.filter(
        (i) => (i.flavorQueryParam ?? '').trim().toLowerCase() === requestedFlavor
      );
    } else {
      // Requested flavor is NOT available on this PDP (e.g., Omega)
      variantIdsFilteredByFlavor = hasDefaultLane
        ? items.filter((i) => !i.flavorQueryParam)
        : items;
    }
  } else {
    // No specific flavor requested → prefer default lane if available
    variantIdsFilteredByFlavor = hasDefaultLane ? items.filter((i) => !i.flavorQueryParam) : items;
  }

  return variantIdsFilteredByFlavor.map((item) => {
    const attributes = [
      ...(sellingPlanToSubmit && parseInt(sellingPlanToSubmit, 10) > 0
        ? [
            {
              key: '__shop_subscriptions',
              value: 'true'
            }
          ]
        : []),
      ...(item.flavorQueryParam
        ? [
            {
              key: '__flavor',
              value: item.flavorQueryParam
            }
          ]
        : [])
    ];

    return {
      merchandiseId: getFormattedShopifyId('ProductVariant', item.variantId),
      quantity: (quantity || selectedVariant?.quantity) ?? 1,
      ...(sellingPlanToSubmit && parseInt(sellingPlanToSubmit, 10) > 0
        ? {
            sellingPlanId: getFormattedShopifyId('SellingPlan', sellingPlanToSubmit)
          }
        : {}),
      attributes
    } as ShopifyCartItem;
  });
};

export const formatPdpCarouselMedia = (
  carouselMedia?: Array<SanityKeyed<CarouselMedia>>
): SanityKeyed<BaseImage | BaseVideoWithPlayer>[] | undefined =>
  carouselMedia?.map(
    (media) =>
      ({
        ...media,
        _type: media._type === 'image' ? 'baseImage' : 'baseVideoWithPlayer',
        fit: media.fit === 'adaptive' ? 'cover' : media.fit
      }) as SanityKeyed<BaseImage | BaseVideoWithPlayer>
  );
